/**
 * DashboardDailyChecksCard.js
 *
 * FT-80 Phase 5: Thin RIBBON-STYLE Daily Checks status bar.
 * Matches the MobileVesselStatusBar design pattern.
 * Simple: Icon + "Daily Checks" + Status Badge + View button
 *
 * Clicking opens Daily Checks directly via onOpenDailyChecks callback
 */

import React from 'react';
import {
  ClipboardCheck,
  CheckCircle2,
  Circle,
  ChevronRight
} from 'lucide-react';
import { useDailyChecks } from '../../../hooks/domain/operations/useDailyChecks';

/**
 * Dashboard Daily Checks Ribbon
 * Thin horizontal bar matching MobileVesselStatusBar style
 *
 * @param {Function} onOpenDailyChecks - Callback to open Daily Checks modal directly
 */
const DashboardDailyChecksCard = ({ onOpenDailyChecks }) => {
  const { getTodaySummary, getCategoriesWithStatus } = useDailyChecks();

  // Get overall completion summary
  const summary = getTodaySummary();
  const categories = getCategoriesWithStatus();

  // If no enabled categories, don't show the ribbon
  if (categories.length === 0) {
    return null;
  }

  const isComplete = summary.completed === summary.total && summary.total > 0;
  const hasStarted = summary.completed > 0;

  const handleClick = () => {
    // FT-80 Phase 6: Open Daily Checks directly (not via Operations)
    if (onOpenDailyChecks) {
      onOpenDailyChecks();
    }
  };

  return (
    <div
      className="mb-4"
      onClick={handleClick}
    >
      <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-sm px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors">
        {/* Left: Icon + Label + Status indicator */}
        <div className="flex items-center gap-3">
          <ClipboardCheck className={`h-5 w-5 ${
            isComplete ? 'text-green-600' : hasStarted ? 'text-amber-600' : 'text-slate-500'
          }`} />
          <span className="font-medium text-slate-800">Daily Checks</span>
          {isComplete && (
            <div className="w-2 h-2 bg-green-500 rounded-full" />
          )}
        </div>

        {/* Center: Status text (only on larger screens) */}
        <div className="hidden sm:flex items-center gap-2 text-sm">
          {isComplete ? (
            <span className="text-green-600">{summary.total}/{summary.total} complete</span>
          ) : hasStarted ? (
            <span className="text-amber-600">{summary.completed}/{summary.total} done</span>
          ) : (
            <span className="text-slate-500">{summary.total} checks today</span>
          )}
        </div>

        {/* Right: Status Badge + Chevron */}
        <div className="flex items-center gap-2">
          {isComplete ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
              <CheckCircle2 className="h-4 w-4" />
              <span className="hidden xs:inline">Done</span>
            </div>
          ) : hasStarted ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium">
              <Circle className="h-4 w-4" />
              <span className="hidden xs:inline">{summary.completed}/{summary.total}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium">
              <span>Start</span>
            </div>
          )}
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </div>
      </div>
    </div>
  );
};

export default DashboardDailyChecksCard;
